import { roundness } from "@provider/dripsy";
import type { DripsyColor } from "@utils";
import { useDripsyColor } from "@utils";
import { Text, useSx, View } from "dripsy";
import AnimatedPressable from "./animated-pressable";
import * as React from "react";

export type ListItemProps = {
  title?: string;
  description?: string;
  left?: any;
  right?: any;
  icon?: React.ReactNode;
  onPress?: () => void;
  titleColor?: DripsyColor;
  descriptionColor?: DripsyColor;
  maxNumberOfLines?: number;
  disabled?: boolean;
};

function ListItem({
  title,
  description,
  left,
  right,
  icon,
  onPress,
  titleColor = "$text",
  descriptionColor = "$textGrey",
  maxNumberOfLines = 1,
  disabled,
  ...rest
}: ListItemProps) {
  const finalTitleColor = useDripsyColor({ color: disabled ? "$grey" : titleColor });
  const finalDescriptionColor = useDripsyColor({ color: disabled ? "$grey" : descriptionColor });

  const sx = useSx();
  return (
    <View
      sx={{
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      <AnimatedPressable
        onPress={disabled ? undefined : onPress}
        animate={React.useMemo(
          () =>
            ({ hovered, pressed }) => {
              return {
                backgroundColor: hovered || pressed ? "rgba(0,0,0,0.05)" : "rgba(0,0,0,0.0)",
              };
            },
          []
        )}
        style={sx({
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingTop: "$2",
          paddingBottom: "$2",
          paddingRight: "$1",
          paddingLeft: "$1",
          borderRadius: roundness,
        })}
        {...rest}
      >
        <View
          sx={{
            flexDirection: "row",
            alignItems: "center",
            flex: 1,
          }}
        >
          {left ? (
            <View
              sx={{
                width: 30,
                alignItems: "center",
                justifyContent: "center",
                marginRight: "$1",
              }}
            >
              {left}
            </View>
          ) : null}
          {icon ? (
            <View sx={{ marginRight: "$2" }}>
              {icon}
            </View>
          ) : null}

          <View sx={{ flex: 1, marginLeft: "$1" }}>
            <Text
              sx={{
                fontSize: [15, 16, 17],
                fontWeight: "bold",
                color: finalTitleColor,
                flexWrap: "wrap",
              }}
              selectable={false}
              numberOfLines={2}
            >
              {title}
            </Text>
            {description ? (
              <Text
                sx={{ fontSize: [13, 14, 15], color: finalDescriptionColor, fontWeight: "400" }}
                selectable={false}
                numberOfLines={maxNumberOfLines}
              >
                {description}
              </Text>
            ) : null}
          </View>
        </View>
      </AnimatedPressable>
      {right ? <View sx={{ position: "absolute", right: 0 }}>{right}</View> : null}
    </View>
  );
}
export default ListItem;
